import { cn } from '@/shared/utils/cn';
import { initialsOf } from '@/shared/utils/initials';

/**
 * R-050 Avatar — an initials chip standing in for a person. No photographs in
 * the product UI.
 *
 * Initials come from the first two words of the name (R-050). In Arabic, two
 * initials written side by side JOIN into a word ("ل" + "ع" reads "لع"); a
 * zero-width non-joiner between them keeps them apart as letters, which is what
 * initials are. A name with no letters shows "?" rather than an empty circle.
 *
 * The chip is decorative wherever the name is written beside it, which is every
 * current use; `title` still carries the full name for a pointer.
 */
export type AvatarSize = 'sm' | 'md' | 'lg';

const SIZE: Record<AvatarSize, string> = {
  sm: 'h-6 w-6',
  md: 'h-7 w-7',
  lg: 'h-9 w-9',
};

export function Avatar({
  name,
  size = 'md',
  onShell = false,
  className,
}: {
  /** Full name; the initials are derived from its first two words. */
  name: string | null | undefined;
  size?: AvatarSize;
  /** The Plum chip, for the application's own chrome. */
  onShell?: boolean;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      title={name ?? undefined}
      className={cn(
        'type-label inline-flex flex-none items-center justify-center rounded-full font-semibold',
        SIZE[size],
        onShell ? 'bg-(--fid-plum) text-ink-on-brand' : 'bg-(--fid-plum-100) text-(--fid-plum)',
        className,
      )}
    >
      {initialsOf(name)}
    </span>
  );
}
